import { matchesIssueLabels, toUserFacingMessage, isAllStatusView } from '@overlayqa/shared';
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useIssues } from '../../hooks/useTickets';
import { useBulkDeleteIssues, useBulkStatusIssues, useBulkIgnoreIssues, useMoveIssues } from '../../hooks/useBulkActions';
import { useIntegrations } from '../../hooks/useIntegrations';
import { useExportDialog } from '../../lib/exportDialog';
import { useAuth } from '../../lib/auth';
import { EXPORT_PROVIDERS, getExportProviderLabel, ACTIVE_ISSUE_STATUSES, isActiveStatusView, applyStatusView, countHiddenByStatusView, type ExportProvider } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { ISSUE_STATUS_OPTIONS } from '../../lib/issueTypeOptions';

/** The Status filter's resting value, the "Active issues" view (@overlayqa/shared). */
export const ACTIVE_STATUS_FILTER: readonly string[] = ACTIVE_ISSUE_STATUSES;

interface IssueFilters {
  status: readonly string[];
  severity: readonly string[];
  type: readonly string[];
  assignee: readonly string[];
  creator: readonly string[];
  labels: readonly string[];
}
const DEFAULT_FILTERS: IssueFilters = { status: ACTIVE_STATUS_FILTER, severity: [], type: [], assignee: [], creator: [], labels: [] };

export function useIssuesPageData(projectId: string | undefined, view: 'table' | 'board' = 'table') {
  const exportWithDialog = useExportDialog();
  const defaults = { ...DEFAULT_FILTERS, status: view === 'board' ? ISSUE_STATUS_OPTIONS.map(option => option.value) : ACTIVE_STATUS_FILTER };
  const [searchQuery, setSearchQuery] = useState('');
  // Every filter is a LIST (checkbox menus), scoped to the project so a
  // project switch returns to the defaults rather than carrying one project's
  // view into another.
  const [scoped, setScoped] = useState<{ projectId: string | undefined; filters: Partial<IssueFilters> }>({ projectId, filters: {} });
  const filters = { ...defaults, ...(scoped.projectId === projectId ? scoped.filters : {}) };
  const setFilter = (key: keyof IssueFilters) => (value: readonly string[]) =>
    setScoped((prev) => ({
      projectId,
      filters: { ...(prev.projectId === projectId ? prev.filters : {}), [key]: value },
    }));
  const statusFilter = filters.status;
  // The shared picker displays its default label when nothing is checked.
  // Match that label: Board defaults to all statuses; Table defaults to active.
  const effectiveStatuses = statusFilter.length ? statusFilter : defaults.status;
  const severityFilter = filters.severity;
  const typeFilter = filters.type;
  const assigneeFilter = filters.assignee;
  const creatorFilter = filters.creator;
  const labelFilter = filters.labels;
  const setLabelFilter = setFilter('labels');
  const setStatusFilter = setFilter('status');
  const setSeverityFilter = setFilter('severity');
  const setTypeFilter = setFilter('type');
  const setAssigneeFilter = setFilter('assignee');
  const setCreatorFilter = setFilter('creator');
  const statusIsDefault = view === 'board' ? ISSUE_STATUS_OPTIONS.every(option => effectiveStatuses.includes(option.value)) : isActiveStatusView(effectiveStatuses);
  const hasChoiceFilters = (!statusIsDefault && !isAllStatusView(effectiveStatuses)) || severityFilter.length > 0
    || typeFilter.length > 0 || assigneeFilter.length > 0 || creatorFilter.length > 0 || labelFilter.length > 0;
  const hasFilters = !!searchQuery.trim() || !statusIsDefault || hasChoiceFilters;
  const clearFilters = () => {
    setSearchQuery('');
    setScoped({ projectId, filters: {} });
  };
  const [confirmDelete, setConfirmDelete] = useState<{ ids: string[] } | null>(null);

  // Project only: the list reads every page, so all five filters (each able to
  // hold several values) apply client-side below.
  const { data: ticketsData, isLoading, isFetching, error, refetch } = useIssues({ projectId }, { refetchInterval: view === 'board' ? 15000 : false });

  const navigate = useNavigate();
  const bulkDelete = useBulkDeleteIssues();
  const bulkStatus = useBulkStatusIssues();
  const bulkIgnore = useBulkIgnoreIssues();
  const moveIssues = useMoveIssues();
  const { data: integrations } = useIntegrations();
  const { getToken } = useAuth();

  // All providers the dashboard supports, with their connection state. The menu
  // surfaces every provider so users can discover integrations even before they
  // connect (matches extension pattern).
  const connectedSet = useMemo(
    () => new Set((integrations ?? []).map((i) => i.provider as ExportProvider)),
    [integrations],
  );
  const availableProviders = useMemo(
    () => EXPORT_PROVIDERS.map((provider) => ({
      provider,
      connected: connectedSet.has(provider),
    })),
    [connectedSet],
  );

  const handleExport = useCallback(async (ids: string[], provider: ExportProvider) => {
    log.info('[IssuesPage] Export click', { count: ids.length, provider, connected: connectedSet.has(provider) });

    if (!connectedSet.has(provider)) {
      toast.info(`Connect ${getExportProviderLabel(provider)} in Integrations to export issues.`);
      navigate('/integrations');
      return;
    }

    try {
      const token = await getToken();
      // Every provider opens the shared destination chooser: the user picks
      // the project / team / workspace / board there, and the chooser owns the
      // request, the per-row outcome and the funnel events. Until 2026-10-22
      // five providers were resolved to the FIRST destination the account
      // could see and exported there with no picker (board P1, 2026-10-22).
      await exportWithDialog(provider, token ?? undefined, ids);
    } catch (err) {
      // Loading the selected issues (or a missing session) throws before the
      // chooser opens, so without this catch that was an unhandled rejection
      // with zero UI feedback. Inside the chooser, failures render in place.
      //
      // warn, not error: fetchApi already filed this failure at its own level
      // (error for an unexpected status, warn for a declared one), so an error
      // here double-filed ONE failure as TWO $exceptions in Error Tracking
      // (measured 2026-09-09 on the 50-id export cap: 2 rows for 1 failure,
      // PostHog scout report 01a086e4). The toast below still surfaces it.
      log.warn('[IssuesPage] Export failed', {
        provider,
        error: err instanceof Error ? err.message : String(err),
      });
      const message = toUserFacingMessage(err, '');
      toast.error(message || `Failed to export to ${getExportProviderLabel(provider)}.`);
    }
  }, [getToken, navigate, connectedSet, exportWithDialog]);

  const allIssues = ticketsData?.items || [];
  // Everything the reviewer's choices leave standing, before Status. ANY ticked
  // value within a filter matches; ALL filters must match. The hidden count
  // below is therefore a whole-project number on this surface.
  const chosenIssues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allIssues.filter((i) =>
      matchesIssueLabels(i.labels, labelFilter) &&
      (!q || i.title.toLowerCase().includes(q)) &&
      (!severityFilter.length || severityFilter.includes(i.severity)) &&
      (!typeFilter.length || typeFilter.includes(i.type)) &&
      (!assigneeFilter.length || assigneeFilter.some((a) => (a === 'unassigned' ? !i.assigneeId : i.assigneeId === a))) &&
      (!creatorFilter.length || (!!i.createdById && creatorFilter.includes(i.createdById))),
    );
  }, [allIssues, searchQuery, severityFilter, typeFilter, assigneeFilter, creatorFilter, labelFilter]);

  // Only the Active default hides anything it was not asked about, so only it
  // reports a hidden count (the "N finished issues hidden" line).
  const hiddenFinishedCount = useMemo(() => countHiddenByStatusView(chosenIssues, effectiveStatuses), [chosenIssues, effectiveStatuses]);
  const issues = useMemo(() => applyStatusView(chosenIssues, effectiveStatuses), [chosenIssues, effectiveStatuses]);

  const showSkeleton = isLoading && !ticketsData;

  const handleDelete = useCallback((ids: string[]) => {
    // The dialog does not await onConfirm; keep refusals in the mutation's
    // error handler and close only after the delete has succeeded.
    bulkDelete.mutate({ ids }, { onSuccess: () => setConfirmDelete(null) });
  }, [bulkDelete]);

  return {
    searchQuery, setSearchQuery,
    statusFilter, setStatusFilter,
    severityFilter, setSeverityFilter,
    typeFilter, setTypeFilter,
    assigneeFilter, setAssigneeFilter,
    creatorFilter, setCreatorFilter,
    labelFilter, setLabelFilter,
    hiddenFinishedCount, hasFilters, hasChoiceFilters, clearFilters,
    confirmDelete, setConfirmDelete,
    allIssues, issues,
    ticketsData,
    isLoading, isFetching, error,
    refetch,
    showSkeleton,
    bulkDelete, bulkStatus, bulkIgnore, moveIssues,
    availableProviders,
    handleExport,
    handleDelete,
  };
}
